
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calculator, DollarSign, Phone } from "lucide-react";
import TVMInputForm from "./tvm/TVMInputForm";
import TVMResults from "./tvm/TVMResults";
import NoteDiscountCalculator from "./tvm/NoteDiscountCalculator";
import QuoteCalculator from "./QuoteCalculator";
import { TVMInputs, TVMResults as TVMResultsType, CalculationType } from "./tvm/types";
import { calculatePayment, calculateRemainingBalance, calculatePeriods, calculateBalloonPayment } from "./tvm/calculations";

interface TVMCalculatorProps {
  onLoadToInvestment?: (data: any) => void;
}

const TVMCalculator = ({ onLoadToInvestment }: TVMCalculatorProps) => {
  const [inputs, setInputs] = useState<TVMInputs>({
    presentValue: 0,
    futureValue: 0,
    interestRate: 0,
    periods: 0,
    payment: 0,
    paymentsPerYear: 12,
    paymentsUntilBalloon: 0,
    balloonPayment: 0
  });
  
  const [calculationType, setCalculationType] = useState<CalculationType>('payment');
  const [results, setResults] = useState<TVMResultsType | null>(null);

  const handleCalculate = () => {
    
    let calculatedResults: TVMResultsType = {};
    
    try {
      switch (calculationType) {
        case 'payment':
          const payment = calculatePayment(inputs);
          calculatedResults.payment = payment;
          
          // If balloon payment is enabled, calculate it
          if (inputs.paymentsUntilBalloon > 0) {
            const balloonAmount = calculateBalloonPayment(inputs);
            calculatedResults.balloonPayment = balloonAmount;
          }
          break;
          
        case 'balance':
          const balance = calculateRemainingBalance(inputs);
          calculatedResults.remainingBalance = balance;
          break;
          
        case 'periods':
          const periods = calculatePeriods(inputs);
          calculatedResults.periods = periods;
          break;
      }
      
      setResults(calculatedResults);
    } catch (error) {
      console.error('Calculation error:', error);
      setResults(null);
    }
  };

  const handleInputChange = (field: keyof TVMInputs, value: string) => {
    setInputs(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(2)}%`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Calculator className="h-5 w-5" />
          <span>Financial Calculators</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="tvm" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="tvm" className="flex items-center space-x-2">
              <Calculator className="h-4 w-4" />
              <span>TVM Calculator</span>
            </TabsTrigger>
            <TabsTrigger value="note-discount" className="flex items-center space-x-2">
              <DollarSign className="h-4 w-4" />
              <span>Note Discount</span>
            </TabsTrigger>
            <TabsTrigger value="quote-calculator" className="flex items-center space-x-2">
              <Phone className="h-4 w-4" />
              <span>Customer Quotes</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tvm" className="space-y-4">
            <TVMInputForm
              inputs={inputs}
              calculationType={calculationType}
              onInputChange={handleInputChange}
              onCalculationTypeChange={setCalculationType}
              onCalculate={handleCalculate}
            />
            {results && (
              <TVMResults
                results={results}
                inputs={inputs}
                formatCurrency={formatCurrency}
                onCopyToClipboard={() => {}}
                onLoadToInvestment={onLoadToInvestment}
              />
            )}
          </TabsContent>

          <TabsContent value="note-discount" className="space-y-4">
            <NoteDiscountCalculator
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
              onLoadToInvestment={onLoadToInvestment}
            />
          </TabsContent>

          <TabsContent value="quote-calculator" className="space-y-4">
            <QuoteCalculator
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default TVMCalculator;
